import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { F2110, p2201Api } from '../../services/p2201Api';
import { useP2201Mode } from './hooks/useP2201Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useP2201Record } from './hooks/useP2201Record';
import { useP2201Navigation } from './hooks/useP2201Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Detail from './components/Tab2Detail';
import Tab3AmountMemo from './components/Tab3AmountMemo';
import { getStatusBadge, dateToInput, inputToDate, isApproved } from './helpers';
import './P2201Form.css';
import { BatchAcceptanceBridge, pw2201f1AcceptanceManifest } from '../../features/acceptance';

const TAB_LABELS = ['採購入庫', '明細', '金額/備註'];
const NUMBER_TYPE_OPTIONS = [
  { value: '', label: '一般' },
  { value: 'M', label: 'M-膠料' },
  { value: 'P', label: 'P-薄膜' },
];

const P2201Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  // Hooks
  const perms = useModulePermission(Modules.P2201);
  const { mode, setMode, isEditing } = useP2201Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details,
    selectedDetailRow, loading,
    setSelectedDetailRow, setDetails,
    loadRecord, loadCopyTemplate, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    setDetailField, recalcMasterTotals,
    revertToRecord, resetForAdd, applyResult,
  } = useP2201Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useP2201Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWithNames(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load P2201 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: F2110) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.f2110_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, resolveAllNames, setMode]);

  const handleCopyAction = useCallback(async () => {
    if (!record || isEditing) return;
    const template = await loadCopyTemplate(record.f2110_01);
    if (!template) return;
    setMode('add');
    setActiveTab(0);
    clearNames();
    resolveAllNames(template);
    message.info('已建立可編輯的複製草稿；按儲存後才會產生新單號');
  }, [record, isEditing, loadCopyTemplate, setMode, clearNames, resolveAllNames]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除入庫單 ${record.f2110_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  /* Approval mutations intentionally live only in PW2501F1. */
  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將入庫單 ${record.f2110_01} 送審？`,
      okText: '送審', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await p2201Api.submitForApproval(record.f2110_01);
          message.success(resp.message || '送審成功');
          await loadRecordWithNames(record.f2110_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准入庫單 ${record.f2110_01}？`,
      okText: '核准', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await p2201Api.approve(record.f2110_01);
          message.success(resp.message || '核准成功');
          await loadRecordWithNames(record.f2110_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回入庫單 ${record.f2110_01}？`,
      okText: '退回', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await p2201Api.reject(record.f2110_01);
          message.success(resp.message || '退回成功');
          await loadRecordWithNames(record.f2110_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.f2110_01}？沖銷後可重新送審。`,
      okText: '沖銷', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await p2201Api.writeoff(record.f2110_01);
          message.success(resp.message || '沖銷成功');
          await loadRecordWithNames(record.f2110_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleNavTo = useCallback((idx: number) => { navTo(idx, isEditing); }, [navTo, isEditing]);

  // Approval state
  const s80 = (record?.f2110_80 || '').trim();
  const approval: ApprovalState = {
    statusCode: s80,
    isApproved: s80 === 'P',
    isPending: ['D', 'S'].includes(s80), // 統一碼 D + 舊碼 S 過渡容忍
    isLocked: isApproved(record),
  };

  const statusBadge = getStatusBadge(record);

  return (
    <PermissionGuard module={Modules.P2201} action="query">
    <div className="p2201-form">
      {loading && <div className="loading-overlay">載入中...</div>}
      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">採購入庫單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>P2201-採購入庫單</h1>
            <span className="badge badge-default">P2201</span>
            {statusBadge && <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>}
          </div>
        </div>

        {/* Search Bar + Master Header Fields */}
        <div className="search-bar">
          <label>單號</label>
          <input type="text" value={String(getField('f2110_01') || '')} disabled style={{ width: 120 }} data-contract-field="f2110_01" />
          <label>日期</label>
          <input type="date"
            value={dateToInput(getField('f2110_11') as string | null)}
            onChange={e => setField('f2110_11', inputToDate(e.target.value) as F2110[keyof F2110])}
            disabled={!isEditing} style={{ width: 140 }} data-contract-field="f2110_11" />
          <label>跳號別</label>
          <select
            value={String(getField('f2110_31') || '')}
            onChange={e => setField('f2110_31', e.target.value)}
            disabled={!isEditing}
            style={{ width: 100 }} data-contract-field="f2110_31"
          >
            {NUMBER_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <label data-acceptance-non-field="record_navigation_query">快速查詢</label>
          <input
            type="text"
            placeholder="輸入入庫單號"
            disabled={isEditing}
            style={{ width: 140 }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val) {
                  await loadRecordWithNames(val);
                  const idx = allIds.indexOf(val);
                  if (idx >= 0) setCurrentIdx(idx);
                }
              }
            }}
          />
          <span style={{ fontSize: 12, color: '#999' }}>
            {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '無資料'}
          </span>
          <button
            type="button"
            data-contract-action="PW2201F1-ACT-COPY"
            disabled={isEditing || !record || !perms.canCreate}
            onClick={handleCopyAction}
          >
            複製成新單
          </button>
        </div>

        {/* Tabs (3 pages as per Pageframe1) */}
        <div className="tabs">
          <div className="tab-nav">
            {TAB_LABELS.map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData} names={names} isEditing={isEditing}
              getField={getField} setField={setField}
              resolveSingleName={resolveSingleName} />
          </div>
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2Detail
              formData={formData} details={details}
              selectedDetailRow={selectedDetailRow} isEditing={isEditing}
              getField={getField} setDetails={setDetails}
              setSelectedDetailRow={setSelectedDetailRow}
              addDetailRow={addDetailRow} deleteDetailRow={deleteDetailRow}
              setDetailField={setDetailField} />
          </div>
          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3AmountMemo
              formData={formData} isEditing={isEditing}
              getField={getField} setField={setField}
              recalcMasterTotals={recalcMasterTotals} />
          </div>
        </div>

        {/* Bottom Action Bar -- SharedActionBar replaces old ActionBar */}
        <SharedActionBar
          mode={mode} isEditing={isEditing} hasRecord={!!record}
          currentIdx={currentIdx} recordCount={allIds.length}
          onNavFirst={() => handleNavTo(0)} onNavPrev={() => handleNavTo(currentIdx - 1)}
          onNavNext={() => handleNavTo(currentIdx + 1)} onNavLast={() => handleNavTo(allIds.length - 1)}
          onAdd={handleAddAction} onModify={handleModifyAction} onDelete={handleDeleteAction}
          approval={approval}
          onHistory={() => setShowHistory(true)}
          onExit={() => navigate('/erp')}
          canCreate={perms.canCreate} canEdit={perms.canEdit}
          canDelete={perms.canDelete} canApprove={perms.canApprove}
          acceptanceActionIds={{
            create: 'PW2201F1-ACT-CREATE',
            update: 'PW2201F1-ACT-UPDATE',
            delete: 'PW2201F1-ACT-DELETE',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pw2201f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.f2110_01 ? [record.f2110_01] : []}
          keyOf={(item) => item.f2110_01}
          queryExact={(key) => p2201Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>

      {/* Approval History Modal */}
      {record && (
        <ApprovalHistoryModal
          visible={showHistory}
          orderNo={record.f2110_01}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
    </PermissionGuard>
  );
};

export default P2201Form;
